import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react';
import { hcApi, hcError } from './api';
import { HcLayout } from './HcLayout';
import { HcSsrAddress } from './HcSsrAddress';
import { HcSsrTuitionFields, TUITION_FIELDS } from './HcSsrTuitionFields';
import { HcSsrInternetFields, isValidAmount } from './HcSsrInternetFields';

export default function HcSupportiveServicePage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [type, setType] = useState('');
  const [statement, setStatement] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [address, setAddress] = useState({});
  const [confirmed, setConfirmed] = useState(false);
  const [tuition, setTuition] = useState({});
  const [docs, setDocs] = useState({});
  const [amount, setAmount] = useState('');
  const load = () => hcApi.get('/supportive-service-requests').then(({ data: d }) => { setData(d); setAddress(d.address || {}); }).catch((e) => setError(hcError(e)));
  const isTuition = type === 'Tuition Assistance';
  const isInternet = type === 'Internet Service Assistance';
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!type || !statement.trim()) { setError('Choose a support type and add your statement of need.'); return; }
    if (isTuition && TUITION_FIELDS.some(([k]) => !(tuition[k] || '').trim())) { setError('Please fill in all the tuition details.'); return; }
    if (isTuition && (!docs.tuition_bill || !docs.acceptance_letter)) { setError('Please upload your tuition bill and acceptance letter.'); return; }
    if (isInternet && !amount.trim() && !docs.internet_bill) { setError('Please enter the amount requested and upload your internet bill.'); return; }
    if (isInternet && !amount.trim()) { setError('Please enter the amount requested.'); return; }
    if (isInternet && !isValidAmount(amount)) { setError('Please enter the amount requested as a dollar amount, like 75.00.'); return; }
    if (isInternet && !docs.internet_bill) { setError('Please upload your internet bill.'); return; }
    if (!confirmed) { setError('Please confirm your mailing address is correct.'); return; }
    setBusy(true); setError('');
    try {
      const { data: r } = await hcApi.post('/supportive-service-requests', {
        support_type: type, statement, address, address_confirmed: confirmed,
        ...(isTuition ? { tuition, tuition_bill_upload_id: docs.tuition_bill.id, acceptance_letter_upload_id: docs.acceptance_letter.id } : {}),
        ...(isInternet ? { internet_amount: amount, internet_bill_upload_id: docs.internet_bill.id } : {}),
      });
      setDone(r); setType(''); setStatement(''); setTuition({}); setDocs({}); setAmount(''); setConfirmed(false); load();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) { setError(hcError(err)); } finally { setBusy(false); }
  };
  const field = 'w-full rounded-xl border border-gray-200 bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#922A8C]/40';

  return (
    <HcLayout>
      <div className="max-w-3xl hc-fade-up" data-testid="hc-ssr-page">
        <Link to="/helpconnect/resources" className="inline-flex items-center gap-1 text-sm text-[#922A8C] font-semibold" data-testid="hc-ssr-back"><ArrowLeft className="w-4 h-4" />Resources Center</Link>
        <h1 className="text-4xl sm:text-5xl font-extrabold text-[#363636] mt-3">Supportive Service Request</h1>
        {!data && !error && <Loader2 className="w-8 h-8 animate-spin text-[#922A8C] mt-6" />}
        {done && (
          <div className="mt-6 rounded-2xl border border-green-200 bg-green-50 p-5 flex gap-3" data-testid="hc-ssr-confirmation">
            <CheckCircle2 className="w-6 h-6 text-green-600 shrink-0" />
            <div><p className="font-semibold text-green-800">Request {done.ticket.number} submitted</p><p className="text-sm text-green-800 mt-1">{done.confirmation}</p></div>
          </div>
        )}
        {data && !data.eligible && <p className="mt-6 text-gray-600" data-testid="hc-ssr-not-eligible">Please contact your case manager about supportive services.</p>}
        {data?.eligible && (
          <form onSubmit={submit} className="mt-6 bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6 space-y-4" data-testid="hc-ssr-form">
            <div>
              <label className="text-sm font-semibold text-[#363636]">Date</label>
              <input value={data.today} readOnly className={`${field} bg-gray-50 text-gray-600`} data-testid="hc-ssr-date" />
            </div>
            <div>
              <label className="text-sm font-semibold text-[#363636]">Support Type <span className="text-red-600">*</span></label>
              <select value={type} onChange={(e) => setType(e.target.value)} required className={field} data-testid="hc-ssr-type">
                <option value="">Choose a support type</option>
                {data.support_types.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-semibold text-[#363636]">Statement of Need <span className="text-red-600">*</span></label>
              <textarea rows={6} value={statement} onChange={(e) => setStatement(e.target.value)} required maxLength={4000} className={field}
                placeholder="Tell us what you need and why it will help you with your training or job." data-testid="hc-ssr-statement" />
            </div>
            {isTuition && <HcSsrTuitionFields values={tuition} setValues={setTuition} docs={docs} setDocs={setDocs} field={field} />}
            {isInternet && <HcSsrInternetFields amount={amount} setAmount={setAmount} docs={docs} setDocs={setDocs} field={field} />}
            <HcSsrAddress address={address} setAddress={setAddress} confirmed={confirmed} setConfirmed={setConfirmed} field={field} />
            {error && <p className="text-red-600 text-sm" data-testid="hc-ssr-error">{error}</p>}
            <button type="submit" disabled={busy || !confirmed} className="h-12 px-6 rounded-xl bg-[#922A8C] text-white font-semibold hover:bg-[#7a2275] disabled:opacity-60 inline-flex items-center gap-2" data-testid="hc-ssr-submit">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}Submit
            </button>
          </form>
        )}
        {!data?.eligible && error && <p className="text-red-600 mt-6" data-testid="hc-ssr-load-error">{error}</p>}
      </div>
    </HcLayout>
  );
}
